<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import { useUnsavedChangesGuard } from 'dashboard/composables/useUnsavedChangesGuard';
import Button from 'dashboard/components-next/button/Button.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import SettingsPageLayout from 'dashboard/components-next/arrowsAi/settings/SettingsPageLayout.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import UnsavedChangesDialog from 'dashboard/components-next/arrowsAi/UnsavedChangesDialog.vue';

const { t } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant, updateAssistant } =
  useArrowsAiAssistants();
const selected = ref('always');
const savedSnapshot = ref('always');
const isDirty = computed(() => selected.value !== savedSnapshot.value);

const assistant = computed(() => {
  const current = getAssistant(route.params.assistantId);
  if (current) rememberAssistant(current.id);
  return current;
});

const ownSchedule = computed(
  () => assistant.value?.scheduleSource === 'assistant'
);

const options = computed(() => [
  {
    id: 'always',
    label: t('ARROWS_AI.SETTINGS.RESPONSE_WINDOW.ALWAYS.LABEL'),
    description: t('ARROWS_AI.SETTINGS.RESPONSE_WINDOW.ALWAYS.DESC'),
  },
  {
    id: 'business_hours',
    label: t('ARROWS_AI.SETTINGS.RESPONSE_WINDOW.BUSINESS_HOURS.LABEL'),
    description: t('ARROWS_AI.SETTINGS.RESPONSE_WINDOW.BUSINESS_HOURS.DESC'),
  },
  {
    id: 'outside_business_hours',
    label: t('ARROWS_AI.SETTINGS.RESPONSE_WINDOW.OUTSIDE_BUSINESS_HOURS.LABEL'),
    description: t(
      'ARROWS_AI.SETTINGS.RESPONSE_WINDOW.OUTSIDE_BUSINESS_HOURS.DESC'
    ),
  },
]);

watch(
  assistant,
  (current, previous) => {
    if (!current) return;
    if (isDirty.value && previous?.id === current.id) return;
    selected.value = current.responseWindow || 'always';
    savedSnapshot.value = selected.value;
  },
  { immediate: true }
);

const persistSettings = async () => {
  if (!assistant.value) {
    useAlert(t('ARROWS_AI.SETTINGS.ERROR'));
    throw new Error('missing-assistant');
  }

  try {
    await updateAssistant(assistant.value.id, {
      responseWindow: selected.value,
    });
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
    throw error;
  }
  savedSnapshot.value = selected.value;
  useAlert(t('ARROWS_AI.SETTINGS.SUCCESS', { name: assistant.value.name }));
};

const handleSave = () => persistSettings().catch(() => {});
const { dialogRef, confirmSave, discard, stay } = useUnsavedChangesGuard({
  isDirty,
  save: persistSettings,
});
</script>

<template>
  <SettingsPageLayout
    :heading="t('ARROWS_AI.SETTINGS.SCHEDULE.TITLE')"
    :description="t('ARROWS_AI.SETTINGS.SCHEDULE.DESCRIPTION')"
  >
    <MissingAssistant v-if="!assistant" />
    <p v-else-if="ownSchedule" class="mb-0 text-sm text-n-slate-11">
      {{ t('ARROWS_AI.SETTINGS.SCHEDULE.ASSISTANT_NOTE') }}
    </p>
    <div v-else class="flex flex-col gap-4">
      <RadioCard
        v-for="option in options"
        :id="option.id"
        :key="option.id"
        :label="option.label"
        :description="option.description"
        :is-active="selected === option.id"
        active-outline="outline-n-iris-9"
        @select="selected = $event"
      />
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('ARROWS_AI.SETTINGS.SCHEDULE.NOTE') }}
      </p>
      <Button :label="t('ARROWS_AI.SAVE')" @click="handleSave" />
    </div>
  </SettingsPageLayout>
  <UnsavedChangesDialog
    ref="dialogRef"
    @save="confirmSave"
    @discard="discard"
    @stay="stay"
  />
</template>
